import keyBy from 'lodash/keyBy';

import {reporter} from '../error-reporting';
import {datasetsClient} from '../queries/app-api/datasets-client';
import {isBrowserEnv} from './env';

export interface PropertyValueMappings {
  datasets: {
    [datasetId: string]: {name: string; projectId: number};
  };
}

/**
 * Provides explicit configuration of global mapping state for the renamePropertyValue function. This allows
 * callers of renamePropertyValue to retrieve mapped properties values without access to the configurations.
 */
export function setPropertyValueMappings(propertyValueMappings: Partial<PropertyValueMappings>) {
  if (!isBrowserEnv()) {
    return;
  }
  // TODO(RNA-230): Remove dependency on mappings being global by refactoring callsites to pass the mappings to
  // renamePropertyValue.
  // @ts-ignore
  window.__propertyValueMappings = propertyValueMappings;
}

/**
 * Provides the list of global mappings
 */
export function getPropertyValueMappings(): PropertyValueMappings {
  if (!isBrowserEnv()) {
    return null;
  }
  // @ts-ignore
  return window.__propertyValueMappings;
}

/**
 * Returns the promise for loading project mappings
 */
export function getReportPropertyValueMappingsPromise(): Promise<PropertyValueMappings> | null {
  return (window as any).__propertyMappingsPromise || null;
}

/**
 * Updates all property value mappings from different sources
 * prevents querying multiple times to avoid overwritting mappings
 */
export async function syncReportPropertyValueMappings(
  projectId: string | number,
  forceQuery = false,
): Promise<PropertyValueMappings> {
  let mappingsPromise = getReportPropertyValueMappingsPromise();

  if (mappingsPromise !== null && !forceQuery) {
    return mappingsPromise;
  }

  mappingsPromise = datasetsClient
    .query({projectId})
    .catch((err) => {
      reporter.error(err, `Error loading dataset mapping`);
      return {};
    })
    .then((datasets) => ({datasets: keyBy(datasets, `datasetId`)}));

  if (isBrowserEnv()) {
    (window as any).__propertyMappingsPromise = mappingsPromise;
  }

  const propertyValueMappings = await mappingsPromise;
  setPropertyValueMappings(Object.assign({}, getPropertyValueMappings(), propertyValueMappings));
  return propertyValueMappings;
}
